import React from "react";
import { Typography, Grid, Link } from "@mui/material";
import { useNavigate, Link as RouterLink } from "react-router-dom";
import { useTranslation } from "react-i18next";
import HeroSlider from "../components/common/HeroSlider";
import FeatureCard from "../components/common/FeatureCard";
import PageTitle from "../components/common/PageTitle";
import {
  TranslationDebug,
  AdaptiveContainer,
  InfoSection,
  Space,
  UpdatesSection,
  FAQSection,
} from "../components/common";

const Home = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { i18n } = useTranslation();
  const prefix = process.env.PUBLIC_URL || '';

  const mainServices = [
    {
      icon: `${prefix}/svg/DiscoverETAIL.svg`,
      title: t('services.discoverEtail.title'),
      description: t('services.discoverEtail.description'),
      link: '/learn-about',
    },
    {
      icon: `${prefix}/svg/Checkeligibility.svg`,
      title: t('services.discoverEvisa.title'),
      description: t('services.discoverEvisa.description'),
      link: '/learn-about-evisa-b2',
    },
    {
      icon: `${prefix}/svg/ExploreVisaOptions.svg`,
      title: t('services.exploreVisa.title'),
      description: t('services.exploreVisa.description'),
      link: '/visa-option',
    },
    {
      icon: `${prefix}/svg/GetHelp.svg`,
      title: t('services.getHelp.title'),
      description: t('services.getHelp.description'),
      link: '/get-help',
    },
  ];

  const faqData = t("faq.questions", { returnObjects: true });

  return (
    <>
      <PageTitle pageKey="home" titleKey="pageTitles.home" fallbackTitle={t("pageTitles.home")} />
      {/* {process.env.NODE_ENV === 'development' && <TranslationDebug />} */}
      <HeroSlider />

      {/* Section Services Principaux */}
      <Space size="small" />
      <AdaptiveContainer>
        <Grid
          container
          spacing={{ xs: 2, sm: 3, md: 3 }}
          justifyContent="space-between"
          // alignItems="stretch"
        >
          {mainServices.map((service, index) => (
            <Grid
              // item
              // xs={12}
              // sm={6}
              // md={3}
              key={index}
              sx={{
                display: "flex",
                justifyContent: { xs: "stretch", sm: "center", md: "center" },
              }}
            >
              <FeatureCard
                icon={service.icon}
                title={service.title}
                description={service.description}
                onClick={() => navigate(service.link)}
              />
            </Grid>
          ))}
        </Grid>
      </AdaptiveContainer>
      <Space size="small" />
      {/* Section Updates */}
      <UpdatesSection 
        title={t("updates.title")}
        enableApi={true}
        useDirectUrl={true}
      />
      <Space size="small" />
      {/* Section What is ETA-IL avec le nouveau composant InfoSection */}
      <InfoSection
        title={t("etail.title")}
        illustration={`${prefix}/svg/PersonDesktopEtail.svg`}
        // illustrationAlt="Person applying for ETA-IL"
        primaryButton={{
          text: t("etail.startApplication"),
          to: "/apply-for-an-eta-il-1",
          onClick: () => navigate("/apply-for-an-eta-il-1")
        }}
        secondaryButton={{
          text: t("etail.checkEligibility"),
          to: "/EligibilityNational",
          onClick: () => window.location.href = process.env.REACT_APP_SITE+`EligibilityNational?lang=${i18n.language}`,
        }}
        thirdButton={{
          text: t("etail.firstScreen"),
          to: "/group",
          onClick: () => window.location.href = process.env.REACT_APP_SITE+`group?lang=${i18n.language}`,
        }}
      >
        <Typography
          variant="body1"
          sx={{
            lineHeight: 1.6,
            mb: 1,
            fontSize: { xs: "0.95rem", md: "1rem" },
          }}
        >
          {t("etail.description1")}
        </Typography>

        <Typography
          variant="body1"
          sx={{
            lineHeight: 1.6,
            fontSize: { xs: "0.95rem", md: "1rem" },
            mb: 1,
          }}
        >
          {t("etail.description2")} {" "}
          <Typography
            component={RouterLink}
            to="/learn-about"
            sx={{
              color: "#0066CC !important",
              cursor: "pointer",
              fontWeight: 200,
              textDecoration: "none",
              "&:hover": { color: "#0056b3 !important", textDecoration: "underline" },
            }}
          >
            {t("common.learnMore")} {" "}
          </Typography>
        
        </Typography>
        <Typography
          variant="body1"
          sx={{
            lineHeight: 1.6,
            fontSize: { xs: "0.95rem", md: "1rem" },
            mb: 1,
          }}
        >
          {t("etail.description3")}
        </Typography>
        <Typography
          variant="body1"
          sx={{
            lineHeight: 1.6,
            fontSize: { xs: "0.95rem", md: "1rem" },
            mb: 1,
          }}
        >
          {t("etail.description4")}
        </Typography>
      </InfoSection>

      <Space size="small" />

      {/* Section eVisa-B2 avec InfoSection */}
      <InfoSection
        title={t("evisa.title")}
        illustration={`${prefix}/svg/PersonDesktopEvisa.svg`}
        // illustrationAlt="eVisa-B2 Online Application"
        primaryButton={{
          text: t("evisa.applyNow"),
          to: "/apply-for-an-evisa-b2-1",
          onClick: () => navigate("/apply-for-an-evisa-b2-1")
        }}  
      >
        <Typography
          variant="body1"
          sx={{
            lineHeight: 1.6,
            mb: 2,
            fontSize: { xs: "0.95rem", md: "1rem" },
          }}
        >
          {t("evisa.description1")}
        </Typography>

        <Typography
          variant="body1"
          sx={{
            lineHeight: 1.6,
            mb: 2,
            fontSize: { xs: "0.95rem", md: "1rem" },
          }}
        >
          {t("evisa.description2")} {" "}
          <Typography
            component={RouterLink}
            to="/learn-about-evisa-b2"
            sx={{
              color: "#0066CC !important",
              cursor: "pointer",
              fontWeight: 200,
              textDecoration: "none",
              "&:hover": { color: "#0056b3 !important", textDecoration: "underline" },
            }}
          >
            {t("common.learnMore")} {" "}
          </Typography>
        </Typography>

        <Typography
          variant="body1"
          sx={{
            lineHeight: 1.6,
            fontSize: { xs: "0.95rem", md: "1rem" },
            mb: 2,
          }}
        >
          {t("evisa.description3")}
        </Typography>
      </InfoSection>

      <Space size="small" />

      {/* Section FAQ */}
      <FAQSection
        title={t("faq.title")}
        questions={faqData}
        moreQuestionsButton={{
          text: t("faq.moreQuestions"),
          to: "/get-help",
          onClick: () => navigate("/get-help"),
        }}
      />
      <Space size="medium" />
    </>
  );
};

export default Home;
